import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getScenarioById } from '../data/scenarios';
import { uploadImageToServer } from '../utils/imageUpload';
import { analyzeSnap } from '../utils/aiClient';
import PipelineProgress from '../components/PipelineProgress';
import AnalysisResult from '../components/AnalysisResult';
import OutputOptions from '../components/OutputOptions';
import ImageUploadZone from '../components/ui/ImageUploadZone';
import { useToast } from '../components/ui/Toast';
import { useAuth } from '../components/auth/AuthContext';

const ANALYZE_TEXTS = [
  '이미지 읽는 중...',
  'AI 분석 중...',
  '결과 정리 중...',
];

export default function SnapPage() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const scenario = getScenarioById(scenarioId);

  const [stage, setStage] = useState('capture');
  const [progress, setProgress] = useState(0);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [user_question, setUserQuestion] = useState('');

  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeTextIndex, setAnalyzeTextIndex] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState(null);

  const [savedRecord, setSavedRecord] = useState(null);
  const [saving, setSaving] = useState(false);

  // Cycling analysis text
  useEffect(() => {
    if (!analyzing) return;
    const interval = setInterval(() => {
      setAnalyzeTextIndex(prev => (prev + 1) % ANALYZE_TEXTS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [analyzing]);

  if (!scenario) {
    return (
      <div className="page-with-nav" style={{
        minHeight: '100vh',
        background: 'var(--gradient-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-xl)',
          textAlign: 'center',
          animation: 'scaleIn 0.3s ease forwards',
        }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-md)' }}>🔍</div>
          <h2 style={{ color: 'var(--color-text)', marginBottom: 'var(--space-md)' }}>시나리오를 찾을 수 없습니다</h2>
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--color-accent)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            홈으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const isMulti = scenario.capture.acceptMultiple;
  const maxImages = scenario.capture.maxImages || 5;

  const handleFileSelect = (file) => {
    if (isMulti) {
      if (imageFiles.length >= maxImages) {
        toast.warning(`최대 ${maxImages}장까지 첨부 가능합니다.`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setImageFiles(prev => [...prev, file]);
        setImagePreviews(prev => [...prev, ev.target.result]);
      };
      reader.readAsDataURL(file);
      // 단일 이미지도 첫 번째로 설정
      if (!imageFile) {
        setImageFile(file);
        const r2 = new FileReader();
        r2.onload = (ev2) => setImagePreview(ev2.target.result);
        r2.readAsDataURL(file);
      }
    } else {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    }
    setError(null);
  };

  const handleClearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setImageFiles([]);
    setImagePreviews([]);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    if (index === 0) {
      if (imageFiles.length > 1) {
        setImageFile(imageFiles[1]);
        setImagePreview(imagePreviews[1]);
      } else {
        setImageFile(null);
        setImagePreview(null);
      }
    }
  };

  const handleAnalyze = async () => {
    const filesToAnalyze = isMulti && imageFiles.length > 0 ? imageFiles : (imageFile ? [imageFile] : []);
    if (filesToAnalyze.length === 0) {
      toast.warning('이미지를 선택해주세요.');
      return;
    }

    setAnalyzing(true);
    setAnalyzeTextIndex(0);
    setError(null);
    setStage('analyze');

    try {
      setProgress(10);

      // 모든 이미지 업로드
      const imageUrls = [];
      for (let i = 0; i < filesToAnalyze.length; i++) {
        const url = await uploadImageToServer(filesToAnalyze[i], {
          resize: true, maxDimension: 2048, ttl: 86400 * 30,
        });
        imageUrls.push(url);
        setProgress(10 + (20 * (i + 1) / filesToAnalyze.length));
      }

      setProgress(30);

      // 다중 Analyzer 실행 (순차)
      let mergedAnalysis = {};
      for (let ai = 0; ai < scenario.analyzers.length; ai++) {
        const analyzer = scenario.analyzers[ai];
        const analysis = await analyzeSnap({
          imageFile: filesToAnalyze[0],
          scenario_type: scenario.id,
          prompt: analyzer.prompt,
          user_question,
          onProgress: (p) => {
            const base = 30 + (50 * ai / scenario.analyzers.length);
            const range = 50 / scenario.analyzers.length;
            setProgress(Math.min(80, base + p.progress * range / 100));
          },
        });
        mergedAnalysis = { ...mergedAnalysis, ...analysis };
      }

      setProgress(90);
      setAnalysisResult({
        analysis: mergedAnalysis,
        imageUrl: imageUrls[0],
        imageUrls: imageUrls.length > 1 ? imageUrls : undefined,
      });
      setStage('record');
      setProgress(100);
      toast.success('분석이 완료되었습니다!');
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message || '분석 중 오류가 발생했습니다.');
      setStage('capture');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!analysisResult) return;
    setSaving(true);

    try {
      const body = {
        user_id: user.user_id,
        scenario_type: scenario.id,
        image_url: analysisResult.imageUrl,
        images_json: analysisResult.imageUrls || null,
        user_question: user_question || null,
        analysis_json: analysisResult.analysis,
        tags_json: scenario.record.defaultTags,
        level: analysisResult.analysis.detected_level || scenario.record.level || '',
        subject: analysisResult.analysis.detected_subject || scenario.record.subject || '',
        problem_text: analysisResult.analysis.problem_text || analysisResult.analysis.description || '',
        student_answer: analysisResult.analysis.student_answer || '',
        correct_answer: analysisResult.analysis.correct_answer || '',
        explanation: analysisResult.analysis.explanation || analysisResult.analysis.description || '',
        key_concept: analysisResult.analysis.key_concept || '',
        difficulty: analysisResult.analysis.difficulty || analysisResult.analysis.condition || '',
        confidence: analysisResult.analysis.confidence || null,
        checklist_json: analysisResult.analysis.checklist || null,
      };

      const response = await fetch('/api/snap/records', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.success) {
        setSavedRecord({
          record_id: result.data.record_id,
          ...body,
          analysis: analysisResult.analysis,
          created_at: new Date().toISOString(),
        });
        setStage('output');
        toast.success('기록이 저장되었습니다!');
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('저장 실패: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{
        maxWidth: 'var(--max-width)',
        margin: '0 auto',
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-xl)',
        padding: 'var(--space-lg)',
        boxShadow: 'var(--shadow-lg)',
        animation: 'fadeInUp 0.4s ease forwards',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-lg)',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.25rem',
              color: 'var(--color-text)',
            }}
          >
            ←
          </button>
          <span style={{ fontSize: '1.5rem' }}>{scenario.icon}</span>
          <h1 style={{
            margin: 0,
            fontSize: 'var(--font-size-xl)',
            color: 'var(--color-text)',
          }}>
            {scenario.name}
          </h1>
        </div>

        <PipelineProgress currentStage={stage} progress={progress} />

        {/* Stage: Capture */}
        {stage === 'capture' && (
          <div className="animate-fade-in">
            <p style={{
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--space-md)',
              fontSize: 'var(--font-size-sm)',
            }}>
              {scenario.capture.instruction}
            </p>

            <ImageUploadZone
              onFileSelect={handleFileSelect}
              imagePreview={isMulti && imagePreviews.length > 0 ? imagePreviews[0] : imagePreview}
              onClear={handleClearImage}
              instruction={scenario.capture.instruction}
            />

            {/* 다중 이미지 미리보기 */}
            {isMulti && imagePreviews.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '0.5rem',
                overflowX: 'auto',
                padding: '0.5rem 0',
                marginBottom: 'var(--space-sm)',
              }}>
                {imagePreviews.map((preview, i) => (
                  <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={preview}
                      alt={`이미지 ${i + 1}`}
                      style={{
                        width: '64px',
                        height: '64px',
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-md)',
                        border: i === 0 ? '2px solid var(--color-accent)' : '1px solid var(--color-surface-border)',
                      }}
                    />
                    <button
                      onClick={() => handleRemoveImage(i)}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        width: '20px',
                        height: '20px',
                        background: 'var(--color-danger)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        fontSize: '11px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                      }}
                    >
                      x
                    </button>
                  </div>
                ))}
                {imagePreviews.length < maxImages && (
                  <div style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    alignSelf: 'center',
                    whiteSpace: 'nowrap',
                  }}>
                    {imagePreviews.length}/{maxImages}
                  </div>
                )}
              </div>
            )}

            <textarea
              placeholder={scenario.capture.questionPlaceholder}
              value={user_question}
              onChange={(e) => setUserQuestion(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                resize: 'vertical',
                minHeight: '60px',
                marginBottom: 'var(--space-md)',
                fontSize: 'var(--font-size-sm)',
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                transition: 'border-color var(--transition-fast)',
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--color-accent)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--color-surface-border)'}
            />

            <button
              onClick={handleAnalyze}
              disabled={(!imageFile && imageFiles.length === 0) || analyzing}
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                background: (!imageFile && imageFiles.length === 0) || analyzing ? '#ccc' : scenario.color,
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                cursor: (!imageFile && imageFiles.length === 0) || analyzing ? 'not-allowed' : 'pointer',
                transition: 'background var(--transition-fast), transform var(--transition-fast)',
              }}
            >
              {analyzing ? '🤖 AI 분석 중...' : '🔍 AI 분석 시작'}
            </button>
          </div>
        )}

        {/* Stage: Analyze (loading) */}
        {stage === 'analyze' && analyzing && (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-2xl) var(--space-md)',
          }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: 'var(--space-md)',
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              🤖
            </div>
            <p style={{
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--font-size-lg)',
              fontWeight: 500,
              marginBottom: 'var(--space-sm)',
              minHeight: '1.5em',
            }}>
              {ANALYZE_TEXTS[analyzeTextIndex]}
            </p>
            <p style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--space-lg)',
            }}>
              잠시만 기다려주세요
            </p>

            {/* Gradient progress bar */}
            <div style={{
              width: '100%',
              height: '8px',
              background: 'var(--color-surface-border)',
              borderRadius: '4px',
              overflow: 'hidden',
              marginBottom: 'var(--space-sm)',
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--gradient-accent)',
                borderRadius: '4px',
                transition: 'width 0.5s ease',
              }} />
            </div>
            <span style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-accent)',
              fontWeight: 600,
            }}>
              {Math.round(progress)}%
            </span>
          </div>
        )}

        {/* Stage: Record */}
        {stage === 'record' && analysisResult && (
          <div className="animate-fade-in">
            <h2 style={{
              color: scenario.color,
              marginBottom: 'var(--space-md)',
              animation: 'fadeInUp 0.4s ease forwards',
            }}>
              ✅ 분석 완료
            </h2>

            <AnalysisResult
              analysis={analysisResult.analysis}
              scenario_type={scenario.id}
            />

            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                width: '100%',
                padding: 'var(--space-md)',
                marginTop: 'var(--space-lg)',
                background: saving ? '#ccc' : 'var(--color-success)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                transition: 'background var(--transition-fast)',
              }}
            >
              {saving ? (
                <>
                  <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: '1rem' }}>⏳</span>
                  저장 중...
                </>
              ) : '💾 기록 저장'}
            </button>
          </div>
        )}

        {/* Stage: Output */}
        {stage === 'output' && savedRecord && (
          <div className="animate-fade-in">
            <div style={{
              textAlign: 'center',
              padding: 'var(--space-lg)',
              background: 'var(--color-success-bg)',
              borderRadius: 'var(--radius-lg)',
              marginBottom: 'var(--space-lg)',
              animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
            }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 'var(--space-sm)' }}>✅</div>
              <p style={{
                color: 'var(--color-success-text)',
                fontWeight: 700,
                margin: 0,
                fontSize: 'var(--font-size-lg)',
              }}>
                기록이 저장되었습니다!
              </p>
            </div>

            <OutputOptions record={savedRecord} />

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              marginTop: 'var(--space-lg)',
            }}>
              <button
                onClick={() => {
                  setStage('capture');
                  setImageFile(null);
                  setImagePreview(null);
                  setImageFiles([]);
                  setImagePreviews([]);
                  setUserQuestion('');
                  setAnalysisResult(null);
                  setSavedRecord(null);
                  setProgress(0);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: scenario.color,
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  transition: 'opacity var(--transition-fast)',
                }}
              >
                📷 새로운 촬영
              </button>
              <button
                onClick={() => navigate(`/records/${savedRecord.record_id}`)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontWeight: 700,
                  transition: 'opacity var(--transition-fast)',
                }}
              >
                📋 상세 보기
              </button>
            </div>
          </div>
        )}

        {/* Error card */}
        {error && (
          <div style={{
            padding: 'var(--space-md)',
            background: 'var(--color-danger-bg)',
            border: '1px solid #f5c6cb',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-danger-text)',
            marginTop: 'var(--space-md)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            animation: 'fadeInUp 0.3s ease forwards',
          }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>⚠️</span>
            <div style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>{error}</div>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--color-danger)',
                cursor: 'pointer',
                fontSize: '1.2rem',
                padding: 0,
                lineHeight: 1,
                flexShrink: 0,
              }}
            >
              ✕
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
